import { ROLE_LABELS } from '@fos/shared/labels';
import { ChevronDown, Plus, Search, Settings } from 'lucide-react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { type ActiveSession, useActiveSession } from '../../lib/auth';
import { cn } from '../../lib/cn';
import { firstName } from '../../lib/format';
import { useNavBadges } from '../../lib/nav-badges';
import { useScope } from '../../lib/scope';
import { useAccess } from '../../lib/useAccess';
import { Avatar } from '../../ui/Avatar';
import { Badge } from '../../ui/Badge';
import { IconButton } from '../../ui/Button';
import { Kbd, modKeyLabel } from '../../ui/Kbd';
import { LogoSymbol } from '../../ui/Logo';
import { Menu, MenuContent, MenuTrigger } from '../../ui/Menu';
import { Segmented } from '../../ui/Segmented';
import { Tooltip } from '../../ui/Tooltip';
import { ADD_ACTIONS, MORE_NAV, type NavItem, PRIMARY_NAV } from './nav';
import { useOtherWorkspaces } from './other-workspaces';
import { useShell } from './shell-context';
import { UserMenuItems, WorkspaceMenuItems } from './UserMenus';

/**
 * Menu boczne (Figma: Sidebar v2, 3017:1854): 232 px, białe (bg/panel), bez górnego paska; linię border/subtle po
 * prawej rysuje kolumna w AppShell (DECISIONS 2026-10-07 „Nowy układ wizualny”; wcześniej bg/surface bez ramki).
 * Pozycje ukrywane wg `access.modules` z sesji (to tylko UI — backend i tak sprawdza dostęp).
 */
export function Sidebar({ className, onNavigate }: { className?: string; onNavigate?: () => void }) {
  const session = useActiveSession();
  const access = useAccess();
  const badges = useNavBadges();
  const { scope, setScope } = useScope();

  const visible = (item: NavItem) => item.module === null || access.can(item.module);
  const primary = PRIMARY_NAV.filter(visible);
  const more = MORE_NAV.filter(visible);

  return (
    <div className={cn('flex h-full w-[232px] flex-col gap-0.5 bg-panel px-3 pt-[18px] pb-4', className)}>
      <SidebarHeader session={session} onNavigate={onNavigate} />

      {access.isManagerOrAbove && (
        <Segmented
          size="s"
          fullWidth
          aria-label="Zakres danych"
          value={scope}
          onValueChange={setScope}
          options={[
            { value: 'mine', label: 'Moje' },
            { value: 'team', label: 'Zespół' },
          ]}
        />
      )}
      <div className="h-3 shrink-0" />

      <nav aria-label="Menu główne" className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
        {primary.map((item) => (
          <SidebarLink
            key={item.key}
            item={item}
            badge={item.module ? badges[item.module] : undefined}
            onNavigate={onNavigate}
          />
        ))}
        {more.length > 0 && (
          <>
            <div className="pt-[22px] pb-1.5 pl-2.5 text-caption font-medium text-fg-muted">Więcej</div>
            {more.map((item) => (
              <SidebarLink
                key={item.key}
                item={item}
                badge={item.module ? badges[item.module] : undefined}
                onNavigate={onNavigate}
              />
            ))}
          </>
        )}
      </nav>

      <SidebarUser session={session} onNavigate={onNavigate} />
    </div>
  );
}

/** Najdłuższa nazwa workspace'u, która mieści się w jednym wierszu z wyszukiwaniem i „+” (ok. 59 px tekstu). */
export const WORKSPACE_NAME_INLINE_MAX = 8;

function SidebarHeader({ session, onNavigate }: { session: ActiveSession; onNavigate?: () => void }) {
  const { openCommandPalette } = useShell();
  const access = useAccess();
  const addActions = ADD_ACTIONS.filter((a) => access.can(a.module, 'edit'));
  const mod = modKeyLabel();
  // 8.10: co czeka w innych workspace'ach osoby (plakietka przy nazwie; szczegóły w menu i na Dziś)
  const waiting = useOtherWorkspaces().data.totalWaiting;
  const waitingId = useId();

  const name = session.workspace?.name ?? 'Fravity OS';
  // Przegląd Fali 11: obok wyszukiwania i „+” mieści się ok. 8 znaków — dłuższa nazwa („Pracownia Marcina”) dostaje
  // własny wiersz (do 2 linii), a przyciski schodzą pod nią; pełna nazwa także w podpowiedzi (`title`)
  // (albo gdy mimo krótkiej nazwy tekst się nie mieści — pomiar po wyrenderowaniu, szerokie litery)
  const nameRef = useRef<HTMLSpanElement>(null);
  const [overflowFor, setOverflowFor] = useState<string | null>(null);
  const stacked = name.length > WORKSPACE_NAME_INLINE_MAX || overflowFor === name;
  useLayoutEffect(() => {
    const el = nameRef.current;
    if (el && !stacked && el.scrollWidth > el.clientWidth + 1) setOverflowFor(name);
  }, [name, stacked]);

  const tools = (
    <>
      <Tooltip
        content={
          <span className="inline-flex items-center gap-1.5">
            Szukaj <Kbd className="border-transparent bg-transparent text-fg-inverse">{mod} K</Kbd>
          </span>
        }
      >
        <IconButton icon={Search} label="Szukaj" onClick={() => openCommandPalette('all')} />
      </Tooltip>
      {addActions.length > 0 && (
        <Tooltip content="Dodaj">
          <IconButton
            icon={Plus}
            label="Dodaj"
            raised
            onClick={() => {
              onNavigate?.();
              openCommandPalette('add');
            }}
          />
        </Tooltip>
      )}
    </>
  );

  return (
    <div className={cn('flex pr-1 pb-3.5 pl-2.5', stacked ? 'flex-col gap-1' : 'items-center gap-2')}>
      <Menu>
        <MenuTrigger
          className={cn(
            '-ml-1.5 flex min-w-0 items-center gap-2 rounded-md py-1 pr-1 pl-1.5 text-left hover:bg-elevated focus-visible:outline-2 focus-visible:outline-accent',
            stacked && 'self-start',
          )}
          aria-label={`Workspace ${name}: przełącz albo wyloguj`}
          aria-describedby={waiting > 0 ? waitingId : undefined}
          title={name}
        >
          {/* kropka przy logo: w innym workspace'ie osoby coś czeka (liczby w menu, szczegóły na Dziś) */}
          <span className="relative shrink-0">
            <LogoSymbol size={20} />
            {waiting > 0 && (
              <span
                aria-hidden
                className="absolute -top-1 -right-1 size-2.5 rounded-full bg-accent ring-2 ring-panel"
              />
            )}
          </span>
          <span
            ref={nameRef}
            data-testid="workspace-name"
            className={cn('text-label font-semibold text-fg', stacked ? 'line-clamp-2 break-words' : 'truncate')}
          >
            {name}
          </span>
          {waiting > 0 && (
            <span id={waitingId} className="sr-only">
              {`W innych workspace'ach czeka: ${waiting}`}
            </span>
          )}
          <ChevronDown size={14} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
        </MenuTrigger>
        <MenuContent className="w-60">
          <WorkspaceMenuItems session={session} onNavigate={onNavigate} />
        </MenuContent>
      </Menu>
      {stacked ? (
        <div className="flex items-center justify-end gap-2">{tools}</div>
      ) : (
        <>
          <span className="flex-1" />
          {tools}
        </>
      )}
    </div>
  );
}

function SidebarLink({
  item,
  badge,
  onNavigate,
}: {
  item: NavItem;
  badge?: { kind: 'muted' | 'unread' | 'attention'; count: number };
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const emphasized = badge && badge.count > 0 && badge.kind !== 'muted';
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group flex h-[34px] shrink-0 items-center gap-3 rounded-[7px] px-2.5 text-body-s transition-colors',
          'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
          isActive ? 'bg-elevated font-medium text-fg' : 'text-fg-secondary hover:bg-elevated/60 hover:text-fg',
          emphasized && 'font-semibold text-fg',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            size={18}
            strokeWidth={1.75}
            aria-hidden
            className={cn(
              'shrink-0',
              isActive || emphasized ? 'text-fg' : 'text-fg-muted group-hover:text-fg-secondary',
            )}
          />
          <span className="min-w-0 flex-1 truncate">
            {item.label}
            {/* liczba dla czytników w tym samym elemencie co nazwa: „Inbox, nowe: 3” (plakietka jest aria-hidden) */}
            {badge && badge.count > 0 && (
              <span className="sr-only">
                {badge.kind === 'attention'
                  ? ', do zrobienia: '
                  : badge.kind === 'muted'
                    ? ', do odbioru: '
                    : ', nowe: '}
                {badge.count}
              </span>
            )}
          </span>
          {badge && badge.count > 0 && <Badge kind={badge.kind} count={badge.count} />}
        </>
      )}
    </NavLink>
  );
}

function SidebarUser({ session, onNavigate }: { session: ActiveSession; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const role = session.membership?.role;
  return (
    <div className="flex items-center gap-2.5 pt-3 pr-1 pl-2.5">
      <Menu>
        <MenuTrigger
          className="-my-1 -ml-1.5 flex min-w-0 flex-1 items-center gap-2.5 rounded-md py-1 pl-1.5 text-left hover:bg-elevated focus-visible:outline-2 focus-visible:outline-accent"
          aria-label={`Konto: ${session.user.name}`}
        >
          <Avatar initials={session.user.initials} color={session.user.avatarColor} />
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-body-s text-fg-secondary">{firstName(session.user.name)}</span>
            <span className="truncate text-caption text-fg-muted">{role ? ROLE_LABELS[role] : 'Bez dostępu'}</span>
          </span>
        </MenuTrigger>
        <MenuContent side="top" className="w-60">
          <UserMenuItems session={session} onNavigate={onNavigate} />
        </MenuContent>
      </Menu>
      <Tooltip content="Ustawienia">
        <IconButton
          icon={Settings}
          label="Ustawienia"
          onClick={() => {
            navigate('/ustawienia/profil');
            onNavigate?.();
          }}
        />
      </Tooltip>
    </div>
  );
}
